import { useView, PLATFORM } from 'aurelia-framework';
import { MdcMenu } from '@aurelia-mdc-web/menu';
import {
  GridFilterSetValue,
  IGridFilterColumn,
  IGridFilterCondition,
  IGridFilterOperator,
  operatorNeedsValue,
  operatorsForDataType
} from '../../grid-filter/grid-filter-model';

export interface IGridFiltersDialogModel {
  columns: IGridFilterColumn[];
  conditions: IGridFilterCondition[];
}

interface ISetOption {
  value: GridFilterSetValue;
  label: string;
  selected: boolean;
}

interface IConditionRow {
  condition: IGridFilterCondition;
  operators: IGridFilterOperator[];
  needsValue: boolean;
  isDate: boolean;
  isSet: boolean;
  valueType: string;
  options: ISetOption[];
  valuesText: string;
  valuesAnchor?: HTMLElement;
  valuesMenu?: MdcMenu;
}

@useView(PLATFORM.moduleName('elements/dialogs/grid-filters-dialog/grid-filters-dialog.html'))
export class GridFiltersDialog {
  model: IGridFiltersDialogModel;
  rows: IConditionRow[];

  activate(model: IGridFiltersDialogModel) {
    this.model = model;
    if (!model.conditions.length) {
      model.conditions.push({});
    }
    this.rows = model.conditions.map(x => this.createRow(x));
  }

  addCondition() {
    const condition: IGridFilterCondition = { joinWithPrevious: 'AND' };
    this.model.conditions.push(condition);
    this.rows.push(this.createRow(condition));
  }

  removeCondition(index: number) {
    this.model.conditions.splice(index, 1);
    this.rows.splice(index, 1);
    if (this.model.conditions.length) {
      this.model.conditions[0].joinWithPrevious = undefined;
    }
  }

  columnChanged(row: IConditionRow, colId?: string) {
    if (colId) {
      row.condition.colId = colId;
      row.condition.value = undefined;
      row.condition.values = undefined;
    }
    const column = this.model.columns.find(x => x.colId === row.condition.colId);
    row.operators = column ? operatorsForDataType(column.dataType) : [];
    if (row.condition.operator && !row.operators.some(x => x.value === row.condition.operator)) {
      row.condition.operator = undefined;
    }
    row.isDate = column?.dataType === 'date' || column?.dataType === 'dateString';
    row.isSet = column?.dataType === 'set';
    if (row.isSet && !row.condition.operator) {
      row.condition.operator = 'in';
    }
    row.valueType = column?.dataType === 'number' ? 'number' : 'text';
    row.options = (column?.options ?? []).map(x => ({
      value: x.value,
      label: x.label,
      selected: !!row.condition.values?.includes(x.value)
    }));
    row.valuesText = this.selectedLabels(row);
    row.needsValue = operatorNeedsValue(row.condition.operator);
  }

  operatorChanged(row: IConditionRow, operator: string) {
    row.condition.operator = operator;
    row.needsValue = operatorNeedsValue(operator);
  }

  setValuesChanged(row: IConditionRow) {
    row.condition.values = row.options.filter(x => x.selected).map(x => x.value);
    row.valuesText = this.selectedLabels(row);
  }

  private selectedLabels(row: IConditionRow): string {
    return row.options.filter(x => x.selected).map(x => x.label).join(', ');
  }

  private createRow(condition: IGridFilterCondition): IConditionRow {
    const row: IConditionRow = {
      condition,
      operators: [],
      needsValue: false,
      isDate: false,
      isSet: false,
      valueType: 'text',
      options: [],
      valuesText: ''
    };
    this.columnChanged(row);
    return row;
  }
}
